import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, RefreshCw, FileText, Image as ImageIcon, ExternalLink, Sparkles, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * AdminPaymentProofs — admin reviews payment proofs uploaded by clients
 * (transferência bancária / MBWay). Slot stays blocked indefinitely while
 * pending. Approving marks the booking paid + confirmed; rejecting cancels
 * the booking, frees the slot, and emails the client with the reason.
 */
export default function AdminPaymentProofs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  async function load() {
    try {
      const { data } = await api.get("/admin/bookings/payment-proofs/pending");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  async function approve(bid) {
    setProcessing(p => ({ ...p, [bid]: "approving" }));
    try {
      await api.post(`/admin/bookings/${bid}/payment-proof/approve`);
      toast.success("Pagamento confirmado. Reserva confirmada e cliente notificado.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProcessing(p => ({ ...p, [bid]: null }));
    }
  }

  async function confirmReject() {
    if (!rejectingId) return;
    setProcessing(p => ({ ...p, [rejectingId]: "rejecting" }));
    try {
      await api.post(`/admin/bookings/${rejectingId}/payment-proof/reject`, {
        reason: rejectReason.trim() || null,
      });
      toast.success("Comprovante rejeitado. Reserva cancelada e cliente notificado.");
      setRejectingId(null);
      setRejectReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProcessing(p => ({ ...p, [rejectingId]: null }));
    }
  }

  async function analyze(bid) {
    setProcessing(p => ({ ...p, [bid]: "analyzing" }));
    try {
      const { data } = await api.post(`/admin/bookings/${bid}/payment-proof/analyze`);
      const verdict = data.verdict;
      if (verdict === "match") toast.success(`Match (${data.score}/100) — todos os pontos batem certo.`);
      else if (verdict === "partial") toast.message(`Match parcial (${data.score}/100) — reveja antes de aprovar.`);
      else toast.error(`Sem correspondência (${data.score}/100) — reveja com cuidado.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProcessing(p => ({ ...p, [bid]: null }));
    }
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-payment-proofs">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow text-[#d4af37]">Pagamentos manuais</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Comprovantes a validar.</h1>
          <p className="text-sm text-[#a0b2af] mt-3 max-w-xl leading-relaxed">
            Reservas em que o cliente já anexou o comprovante (transferência ou MBWay).
            <strong className="text-[#fdfbf7]"> O horário continua bloqueado</strong> até confirmar
            ou rejeitar. Aprove para marcar como pago e confirmar a reserva — rejeite e
            o slot liberta-se automaticamente (e o cliente recebe email com o motivo).
          </p>
        </div>
        <button onClick={load}
                className="btn-ghost-gold px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                data-testid="proofs-refresh">
          <RefreshCw size={14} /> Atualizar
        </button>
      </header>

      {loading ? (
        <div className="text-sm text-[#a0b2af]">A carregar…</div>
      ) : items.length === 0 ? (
        <div className="lux-card p-8 text-center" data-testid="proofs-empty">
          <CheckCircle2 size={32} className="text-[#10b981] mx-auto mb-3" />
          <div className="font-display text-2xl">Nenhum comprovante pendente.</div>
          <div className="text-sm text-[#a0b2af] mt-2">
            Quando um cliente enviar um comprovante de pagamento, aparece aqui.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(b => (
            <ProofCard key={b.id}
                       booking={b}
                       processing={processing[b.id]}
                       onApprove={() => approve(b.id)}
                       onAnalyze={() => analyze(b.id)}
                       onRejectStart={() => { setRejectingId(b.id); setRejectReason(""); }} />
          ))}
        </div>
      )}

      {rejectingId && (
        <>
          <div className="fixed inset-0 z-40 bg-black/60" onClick={() => setRejectingId(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="lux-card bg-[#041f1b] border border-[#ef4444]/40 w-full max-w-md pointer-events-auto"
                 data-testid="reject-modal">
              <div className="px-5 py-4 border-b border-[#1a4640]">
                <div className="eyebrow text-[#ef4444]">Rejeitar comprovante</div>
                <div className="text-xs text-[#a0b2af] mt-1">
                  A reserva será cancelada e o cliente receberá um email com o motivo.
                  O horário fica imediatamente disponível para outras reservas.
                </div>
              </div>
              <div className="p-5 space-y-3">
                <label className="text-xs uppercase tracking-widest text-[#a0b2af]">
                  Motivo (visível para o cliente · opcional)
                </label>
                <textarea value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          rows={4}
                          maxLength={500}
                          placeholder="Ex.: Comprovante ilegível · valor não corresponde · referência incorreta…"
                          className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#ef4444] focus:outline-none resize-none"
                          data-testid="reject-reason" />
              </div>
              <div className="px-5 py-4 border-t border-[#1a4640] flex justify-end gap-2">
                <button onClick={() => setRejectingId(null)}
                        className="px-4 py-2 text-xs uppercase tracking-widest border border-[#1a4640] hover:border-[#a0b2af] text-[#a0b2af] transition-colors"
                        data-testid="reject-cancel">
                  Cancelar
                </button>
                <button onClick={confirmReject}
                        disabled={processing[rejectingId] === "rejecting"}
                        className="px-4 py-2 text-xs uppercase tracking-widest bg-[#ef4444] hover:bg-[#dc2626] text-white transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                        data-testid="reject-confirm">
                  {processing[rejectingId] === "rejecting"
                    ? <><Loader2 size={14} className="animate-spin" /> A rejeitar…</>
                    : <><XCircle size={14} /> Rejeitar e cancelar reserva</>}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ProofCard({ booking: b, processing, onApprove, onAnalyze, onRejectStart }) {
  const isPdf = (b.payment_proof_content_type || "").includes("pdf")
             || /\.pdf$/i.test(b.payment_proof_filename || "");
  const methodLabel = b.payment_method === "mbway"
    ? "MBWay"
    : b.payment_method === "transferencia"
      ? "Transferência bancária"
      : "—";
  const total = Number(b.total || 0);
  const proofUrl = b.payment_proof_url
    ? `${process.env.REACT_APP_BACKEND_URL || ""}${b.payment_proof_url}`
    : null;
  const ocr = b.payment_proof_ocr || null;

  return (
    <div className="lux-card p-5 border border-[#d4af37]/40" data-testid={`proof-card-${b.id}`}>
      <div className="grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="eyebrow text-[#d4af37]" data-testid={`proof-ref-${b.id}`}>
                {b.payment_reference || b.id.slice(0, 8)}
              </div>
              <div className="font-display text-2xl mt-1 text-[#fdfbf7]">{b.client_name || "—"}</div>
              <div className="text-xs text-[#a0b2af] mt-0.5">
                {b.apt_type} · {b.scheduled_date} às {b.scheduled_time}
                {b.apartment_name && <> · {b.apartment_name}</>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Valor</div>
              <div className="font-display text-3xl text-[#d4af37]">{total.toFixed(2)} €</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 text-xs">
            <div className="bg-[#062c27] border border-[#1a4640] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Método</div>
              <div className="text-[#fdfbf7] mt-1">{methodLabel}</div>
            </div>
            <div className="bg-[#062c27] border border-[#1a4640] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Ref. comprovante</div>
              <div className="text-[#fdfbf7] mt-1 font-mono break-all">
                {b.payment_proof_reference || "—"}
              </div>
            </div>
            <div className="bg-[#062c27] border border-[#1a4640] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Email</div>
              <div className="text-[#fdfbf7] mt-1 break-all">{b.client_email || "—"}</div>
            </div>
            <div className="bg-[#062c27] border border-[#1a4640] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Recebido em</div>
              <div className="text-[#fdfbf7] mt-1">
                {b.payment_proof_uploaded_at ? new Date(b.payment_proof_uploaded_at).toLocaleString("pt-PT") : "—"}
              </div>
            </div>
          </div>

          {ocr && (
            <div className={`p-3 rounded-sm border text-xs space-y-1.5 ${
              ocr.verdict === "match"
                ? "border-[#10b981]/50 bg-[#10b981]/10"
                : ocr.verdict === "partial"
                  ? "border-[#f59e0b]/50 bg-[#f59e0b]/10"
                  : "border-[#ef4444]/50 bg-[#ef4444]/10"
            }`} data-testid={`proof-ocr-${b.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {ocr.verdict === "match"
                    ? <CheckCircle2 size={14} className="text-[#10b981]" />
                    : <AlertTriangle size={14} className="text-[#f59e0b]" />}
                  <span className="uppercase tracking-widest text-[10px] font-semibold">
                    {ocr.verdict === "match" ? "Match IA" : ocr.verdict === "partial" ? "Parcial" : "Sem correspondência"}
                  </span>
                </div>
                <span className="font-mono">{ocr.score}/100</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <CheckRow label="Valor"
                          ok={ocr.checks?.amount_match}
                          detail={ocr.checks?.extracted_amount != null
                            ? `${Number(ocr.checks.extracted_amount).toFixed(2)} € (esperado ${Number(ocr.checks.expected_amount).toFixed(2)} €)`
                            : "não detetado"} />
                <CheckRow label="Referência"
                          ok={ocr.checks?.reference_match}
                          detail={ocr.checks?.expected_reference || "—"} />
                <CheckRow label="Destinatário"
                          ok={ocr.checks?.recipient_match}
                          detail={ocr.checks?.method || "—"} />
              </div>
              {ocr.extracted?.sender_name && (
                <div className="text-[#a0b2af] mt-1">
                  Ordenante detetado: <span className="text-[#fdfbf7]">{ocr.extracted.sender_name}</span>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 flex-wrap pt-1">
            <button onClick={onApprove}
                    disabled={!!processing}
                    className="btn-gold px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid={`proof-approve-${b.id}`}>
              {processing === "approving"
                ? <><Loader2 size={14} className="animate-spin" /> A confirmar…</>
                : <><CheckCircle2 size={14} /> Confirmar pagamento</>}
            </button>
            <button onClick={onAnalyze}
                    disabled={!!processing}
                    title="Analisar com IA"
                    className="px-5 py-2.5 text-xs uppercase tracking-widest border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    data-testid={`proof-analyze-${b.id}`}>
              {processing === "analyzing"
                ? <><Loader2 size={14} className="animate-spin" /> A analisar…</>
                : <><Sparkles size={14} /> {ocr ? "Reanalisar" : "Analisar com IA"}</>}
            </button>
            <button onClick={onRejectStart}
                    disabled={!!processing}
                    className="px-5 py-2.5 text-xs uppercase tracking-widest border border-[#ef4444]/60 text-[#fca5a5] hover:bg-[#ef4444]/10 transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                    data-testid={`proof-reject-${b.id}`}>
              <XCircle size={14} /> Rejeitar
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Comprovante</div>
          {proofUrl && !isPdf && (
            <a href={proofUrl} target="_blank" rel="noreferrer"
               className="block border border-[#1a4640] hover:border-[#d4af37] transition-colors"
               data-testid={`proof-image-link-${b.id}`}>
              <img src={proofUrl} alt="Comprovante"
                   className="w-full h-40 object-cover bg-[#062c27]" />
            </a>
          )}
          {proofUrl && isPdf && (
            <a href={proofUrl} target="_blank" rel="noreferrer"
               className="flex items-center gap-2 p-4 border border-[#1a4640] hover:border-[#d4af37] transition-colors text-sm text-[#d4af37]"
               data-testid={`proof-pdf-link-${b.id}`}>
              <FileText size={20} />
              <span className="flex-1 truncate">{b.payment_proof_filename || "Comprovante PDF"}</span>
              <ExternalLink size={14} />
            </a>
          )}
          {proofUrl && (
            <a href={proofUrl} target="_blank" rel="noreferrer"
               className="text-[10px] uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] inline-flex items-center gap-1"
               data-testid={`proof-open-${b.id}`}>
              {isPdf ? <FileText size={11} /> : <ImageIcon size={11} />} Abrir em nova janela
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function CheckRow({ label, ok, detail }) {
  return (
    <div className="bg-black/20 border border-current/20 rounded-sm p-2">
      <div className="flex items-center gap-1 uppercase tracking-widest text-[9px] opacity-80">
        {ok
          ? <CheckCircle2 size={10} className="text-[#10b981]" />
          : <XCircle size={10} className="text-[#fca5a5]" />}
        {label}
      </div>
      <div className="text-[10px] mt-0.5 truncate" title={detail}>{detail}</div>
    </div>
  );
}

